import { useQuery } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import {
  cmdbImportPreview,
  cmdbImportRun,
  cmdbImportShellQuery,
  cmdbImportTemplate,
  type ImportOutcome,
  type ImportPreview,
} from '../api/cmdb'
import { ApiError } from '../api/client'
import { moduleOf, shellQuery } from '../api/shell'
import { Forbidden } from '../ui/Forbidden'
import { toast } from '../ui/notify'
import { Content } from '../ui/Shell'
import { Spinner } from '../ui/Spinner'
import { usePageStyles } from '../ui/usePageStyles'

/**
 * `Cmdb::import` - intrarea in masa dintr-o foaie de calcul, la /app/cmdb/import.
 *
 * Adresa e cea din portal, litera cu litera: butonul „Import" din lista CMDB
 * trimite acolo, si asa trimit si semnele de carte ale operatorilor.
 *
 * DOI PASI, SI PRIMUL NU SCRIE NIMIC
 *
 * Alegerea fisierului porneste singura verificarea - dupa ce ai ales un fisier,
 * a-l verifica e singurul lucru pe care il vrea cineva, deci a doua apasare era
 * o hotarare pe care n-o avea nimeni de luat. Scrisul ramane o fapta aparte:
 * butonul „Import" e altul, si se apasa dupa ce s-a vazut ce contine fisierul.
 *
 * FISIERUL PLEACA DE DOUA ORI, si nu se vede.
 *
 * Portalul tinea randurile incuviintate in sesiune si le scria de acolo. v4
 * n-are sesiune, iar o lista de randuri trimisa inapoi din browser ar fi fost
 * clientul spunand serverului ce e voie sa se scrie - inclusiv sub ce client.
 * Deci fisierul se urca iar, si serverul trece iar prin toate regulile. Omul nu
 * are ce face altfel: browserul tine deja fisierul in mana.
 */

const STYLES = ['css/adm.css']

/** Ce inseamna fiecare coloana - aceleasi cuvinte ca in macheta portalului. */
const NOTES: Record<string, string> = {
  name: 'required',
  class: 'required — must match a class below',
  company: 'the customer name as it appears in the platform',
  criticality: 'Critical, Important, Standard, Minor, Negligible — or 1 to 5',
  status: 'Operational by default',
  owner: 'the email address of an existing user',
  serial: 'the S/N on the label — searchable from the CMDB list',
  part_number: 'the manufacturer part number, if the sheet has one',
}

export function CmdbImportPage() {
  const shell = useQuery(shellQuery(moduleOf('/cmdb')))
  usePageStyles(STYLES, shell.data?.version ?? '')
  const page = useQuery(cmdbImportShellQuery)

  const input = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [checked, setChecked] = useState<ImportPreview | null>(null)
  const [done, setDone] = useState<ImportOutcome | null>(null)
  const [busy, setBusy] = useState(false)

  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (!page.data) return <Spinner />

  const shellData = page.data

  const choose = (picked: File | null) => {
    setFile(picked)
    setChecked(null)
    setDone(null)
    if (picked === null) return

    if (picked.size > shellData.maxBytes) {
      toast('That file is larger than 8 MB. Split it, or save it as .csv.', 'CMDB', 'error')
      return
    }

    setBusy(true)
    void cmdbImportPreview(picked)
      .then((result) => {
        setBusy(false)
        if (result.status !== 'OK') {
          setChecked(result)
          toast(result.message ?? 'That file could not be read.', 'CMDB', 'error')
          return
        }
        setChecked(result)
      })
      .catch((error: unknown) => {
        setBusy(false)
        toast(error instanceof ApiError ? error.message : 'That file could not be read.', 'CMDB', 'error')
      })
  }

  const write = () => {
    if (file === null || checked === null || checked.valid < 1) return
    setBusy(true)
    void cmdbImportRun(file)
      .then((result) => {
        setBusy(false)
        setDone(result)
        if (result.status !== 'OK') {
          toast(result.message ?? 'Nothing was imported.', 'CMDB', 'error')
          return
        }
        toast(result.message ?? 'Imported.', 'CMDB')
        /* Fisierul se scoate din camp: urcat a doua oara ar face inca o data
           aceleasi elemente, si nimic din ecran n-ar spune ca s-a intamplat. */
        setFile(null)
        setChecked(null)
        if (input.current !== null) input.current.value = ''
      })
      .catch((error: unknown) => {
        setBusy(false)
        toast(error instanceof ApiError ? error.message : 'That could not be imported.', 'CMDB', 'error')
      })
  }

  return (
    <Content>
      <div className="row">
        <div className="col-md-12">
          <div className="alert alert-info">
            <i className="fas fa-info-circle"></i> <strong>What this does.</strong> Creates
            configuration items from a spreadsheet — the services and applications that no scan can
            find, because they exist on an organisation chart rather than on a network.
            <br />
            <br />
            Nothing is written until you have seen what the file contains. Up to{' '}
            {shellData.maxRows.toLocaleString('en-GB')} rows per file.
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-md-5">
          <div className="adm-card">
            <div className="adm-card-head">
              <h5>1. The file</h5>
              <button
                type="button"
                className="adm-card-link btn btn-link"
                id="cmdbImportTemplate"
                onClick={() => {
                  void cmdbImportTemplate().catch(() =>
                    toast('The template could not be downloaded.', 'CMDB', 'error'),
                  )
                }}
              >
                <i className="fa fa-download"></i> Template
              </button>
            </div>

            <input
              type="file"
              ref={input}
              id="cmdbImportFile"
              accept={shellData.extensions.map((e) => '.' + e).join(',')}
              className="adm-file-input"
              onChange={(event) => choose(event.target.files?.[0] ?? null)}
            />
            <label htmlFor="cmdbImportFile" className="btn btn-success">
              <i className="fa fa-folder-open"></i> Choose a file
            </label>
            <span className="adm-file-name" id="cmdbImportName">
              {file === null
                ? `no file chosen · ${shellData.extensions.map((e) => '.' + e).join(', ')}`
                : file.name}
            </span>

            {shellData.lockedCompany > 0 && (
              <p className="text-muted small" style={{ marginTop: 10, marginBottom: 0 }}>
                You are looking at one customer, so every row is imported for{' '}
                <strong>{shellData.companies[0]?.name ?? 'that customer'}</strong>. A row naming a
                different customer is refused, not moved.
              </p>
            )}

            <hr />

            <p className="text-muted small" style={{ marginBottom: 6 }}>
              Columns recognised — matched by their heading, so the order does not matter:
            </p>

            <table className="table adm-list">
              <tbody>
                {Object.entries(shellData.columns).map(([field, label]) => (
                  <tr key={field}>
                    <td>
                      <strong>{label}</strong>
                    </td>
                    <td className="adm-muted">{NOTES[field] ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <p className="text-muted small" style={{ marginBottom: 4 }}>
              Classes available:
            </p>
            <p className="adm-muted" style={{ fontSize: 12 }}>
              {shellData.classes.map((c) => c.name).join(' · ')}
            </p>
          </div>
        </div>

        <div className="col-md-7">
          <div className="adm-card">
            <div className="adm-card-head">
              <h5>2. What the file contains</h5>
            </div>

            <div id="cmdbImportResult">
              {busy && <Spinner />}

              {!busy && checked === null && done === null && (
                <div className="adm-empty-inline">
                  <i className="fa fa-file-excel"></i> Choose a file on the left. Nothing is written
                  until you press Import.
                </div>
              )}

              {!busy && done !== null && (
                <div className={done.status === 'OK' ? 'alert alert-success' : 'alert alert-danger'}>
                  <strong>{done.message}</strong>
                  {(done.stats?.skipped ?? 0) > 0 && (
                    <div className="small">
                      {done.stats?.skipped} row(s) could not be written and were left alone.
                    </div>
                  )}
                </div>
              )}

              {!busy && checked !== null && (
                <>
                  <p>
                    <strong id="cmdbImportValid">{checked.valid}</strong> row(s) can be imported,{' '}
                    <strong>{checked.invalid.length}</strong> refused.
                  </p>

                  {checked.truncated && (
                    <div className="alert alert-warning">
                      The file has more than {checked.maxRows.toLocaleString('en-GB')} rows. Only the
                      first {checked.maxRows.toLocaleString('en-GB')} were read.
                    </div>
                  )}

                  {checked.preview.length > 0 && (
                    <table className="table adm-list">
                      <thead>
                        <tr>
                          <th>Row</th>
                          <th>Name</th>
                          <th>Class</th>
                          <th>Customer</th>
                        </tr>
                      </thead>
                      <tbody>
                        {checked.preview.map((row) => (
                          <tr key={row.row}>
                            <td className="adm-muted">{row.row}</td>
                            <td>{row.name}</td>
                            <td>{row.class_name}</td>
                            <td>{row.company_name}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

                  {checked.invalid.length > 0 && (
                    <>
                      <p className="text-muted small" style={{ marginBottom: 6 }}>
                        Refused — the row number is the one in your own file:
                      </p>
                      <table className="table adm-list" id="cmdbImportRefused">
                        <tbody>
                          {checked.invalid.map((bad) => (
                            <tr key={bad.row}>
                              <td className="adm-muted">{bad.row}</td>
                              <td>{bad.name}</td>
                              <td>{bad.reason}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}

                  <button
                    type="button"
                    id="cmdbImportRun"
                    className="btn btn-primary"
                    disabled={checked.valid < 1 || busy}
                    onClick={write}
                  >
                    <i className="fa fa-database"></i> Import {checked.valid} item(s)
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </Content>
  )
}
